import * as angular from "angular";
import * as _ from "lodash";
import { ProspectorService } from "../services/prospector.service";
import { CampaignDataProvider } from "./campaign.dp";
import { MapHelperService } from "../components/map/map.helper";
import {
    develop_us_states,
    us_states,
} from "../utils/constants/prospector.constants";

export class ProspectorDataProvider {
    private readonly $q: any;
    private readonly $http: any;
    private readonly service: ProspectorService;
    private readonly $interval: any;
    private countPromise: any;
    private __env: any;
    private mapHelperService: MapHelperService;
    public reloadMap: boolean = false;
    public campaignDataProvider: CampaignDataProvider;
    public selectedState: string;
    public imports: any[] = [];
    public searchDelay: any;
    public searchDelayLength: number = 600;
    public inProgressImportStatuses: number[] = [
        1, 2, 3, 4, 5, 6, 10, 11, 12, 13, 14,
    ];

    constructor(
        $q: any,
        __env: any,
        $http: any,
        $interval: any,
        ProspectorService: ProspectorService,
        CampaignDataProvider: CampaignDataProvider,
        MapHelperService: MapHelperService
    ) {
        this.$q = $q;
        this.__env = __env;
        this.$http = $http;
        this.$interval = $interval;
        this.service = ProspectorService;
        this.campaignDataProvider = CampaignDataProvider;
        this.mapHelperService = MapHelperService;
    }

    count(data: any, noDelay: boolean): Promise<number> {
        const deferred = this.$q.defer();
        if (this.countPromise) this.countPromise.resolve("user cancelled");
        this.countPromise = deferred;

        if (this.searchDelay) {
            this.searchDelay = this.$interval.cancel(this.searchDelay);
        }
        this.searchDelay = this.$interval(
            (): void => {
                this.$interval.cancel(this.searchDelay);
                this.$http({
                    url: "/v2/prospector/l2/count",
                    method: "POST",
                    data: data,
                    timeout: this.countPromise.promise,
                }).then(
                    (response: object) => {
                        let json = response["data"];
                        if (json.success) {
                            deferred.resolve(json.data);
                        } else {
                            deferred.reject(json);
                        }
                    },
                    (error: any) => {
                        deferred.reject(error);
                    }
                );
            },
            noDelay ? 0 : this.searchDelayLength
        );

        return deferred.promise;
    }

    getStates(): { name: string; abbreviation: string }[] {
        if (this.__env.deployment_type == "production") {
            return us_states;
        } else {
            return develop_us_states;
        }
    }

    getSelectedState(): string | null {
        if (this.selectedState) return this.selectedState;

        this.campaignDataProvider.activeCampaign.getLocalStorage();
        if (this.campaignDataProvider.activeCampaign.localInfo.us_state) {
            this.selectedState =
                this.campaignDataProvider.activeCampaign.localInfo.us_state;
            return this.selectedState;
        }

        return this.selectedState;
    }

    getSuggestedState(center: any): any {
        const deferred = this.$q.defer();

        this.mapHelperService.suggestedState(
            center.latitude,
            center.longitude,
            (state: any): void => {
                if (
                    state &&
                    this.isValidState(state.abbreviation) &&
                    state.abbreviation != this.getSelectedState()
                ) {
                    deferred.resolve({
                        name: state.name,
                        abbreviation: state.abbreviation,
                    });
                } else {
                    deferred.reject();
                }
            },
            (): void => {
                deferred.reject();
            }
        );

        return deferred.promise;
    }

    isValidState(stateAbbreviation: string): boolean {
        return this.getStateFromAbbreviation(stateAbbreviation) != null;
    }

    getStateFromAbbreviation(stateAbbreviation: string): null | any {
        let state: any = null;
        angular.forEach(this.getStates(), (us_state) => {
            if (us_state.abbreviation == stateAbbreviation) {
                state = us_state;
            }
        });
        return state;
    }

    getZoomFromKm(width: number): number {
        if (width > 3500) {
            return 5;
        }
        if (width > 2500) {
            return 6;
        }
        if (width > 1000) {
            return 7;
        }
        if (width > 500) {
            return 7;
        }
        if (width > 300) {
            return 8;
        }
        if (width > 200) {
            return 9;
        }
        if (width > 100) {
            return 10;
        }

        return 11;
    }

    setSelectedState(state: string): void {
        this.selectedState = state;
        this.campaignDataProvider.activeCampaign.localInfo.us_state = state;
        this.campaignDataProvider.activeCampaign.saveLocalStorage();
    }

    importInProgress(prospectorImport: any): boolean {
        let statusId = parseInt(prospectorImport.imsid);
        return (
            prospectorImport.improgress < 100 &&
            this.inProgressImportStatuses.includes(statusId)
        );
    }

    importCompleted(prospectorImport: any): boolean {
        let statusId = parseInt(prospectorImport.imsid);
        return prospectorImport.improgress == 100 && statusId == 7;
    }

    importFailed(prospectorImport: any): boolean {
        let statusId = parseInt(prospectorImport.imsid);
        return (
            prospectorImport.imsid != 7 &&
            !this.inProgressImportStatuses.includes(statusId)
        );
    }

    import(data: any): Promise<any> {
        const deferred = this.$q.defer();
        this.service.import(data).then((response: any) => {
            let json = response["data"];
            if (json.success) {
                this.imports.unshift(json.data);
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    getImports(): Promise<any[]> {
        const deferred = this.$q.defer();
        this.service.imports().then((response: any) => {
            let json = response["data"];
            if (json.success) {
                this.imports = json.data;
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    startProgress(id: number): Promise<any[]> {
        const deferred = this.$q.defer();
        this.service.startProgress(id).then((response: any) => {
            let json = response["data"];
            if (json.success) {
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    credits(): Promise<any> {
        const deferred = this.$q.defer();
        this.service.credits().then((response: any) => {
            let json = response["data"];
            if (json.success) {
                deferred.resolve(json.data);
            } else {
                deferred.reject(json);
            }
        });
        return deferred.promise;
    }

    processSocketUpdate(data: any): void {
        if (data && data.imsid) {
            console.log(data.imsid);
        }
        angular.forEach(this.imports, (prospectorImport: any): void => {
            if (prospectorImport.imid == data.imid) {
                _.merge(prospectorImport, data);
            }
        });

        if (data.improgress && data.improgress >= 100) {
            this.reloadMap = true;
        }
    }

    processSocketCreate(data: any): void {
        angular.forEach(this.imports, (prospectorImport: any): void => {
            if (prospectorImport.imid == data.imid) {
                _.merge(prospectorImport, data);
            }
        });

        if (data.improgress && data.improgress >= 100) {
            this.reloadMap = true;
        }
    }
}

angular
    .module("vcDataProvider")
    .service("ProspectorDataProvider", ProspectorDataProvider);
